import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelXSmall} from "@khanacademy/wonder-blocks-typography";
import {
    markMinorConversions,
    usePublishAnalyticsEvent,
    type MinorConversionId,
} from "@repo/analytics-learning-time";
import type {Role, UpdateStep} from "@repo/auth-shared";
import {RequestInfo} from "@repo/shared-core";
import {getLoginUrl, preserveSearchParams} from "@repo/urls";

import BirthdayAndSignupSubform from "#auth/components/birthday-and-signup-subform.tsx";
import {isKhanmigoSignupLearnerParentSignup} from "#auth/components/util.ts";
import styleConstants from "#auth/style-constants.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedButtonToggle from "#components/button-toggle-package/deprecated-button-toggle.tsx";

import AuthTitle from "../auth-title.tsx";
import ProgressShellActionScreen from "../progress-shell/progress-shell-action-screen.tsx";

const ROLES: Array<Role> = ["learner", "teacher", "parent"];
const cedarRole: Record<Role, "LEARNER" | "TEACHER" | "PARENT"> = {
    learner: "LEARNER",
    teacher: "TEACHER",
    parent: "PARENT",
};

const getRoleCopy = () => ({
    learner: t`Learner`,
    teacher: t`Teacher`,
    parent: t`Parent`,
});

type Props = {
    continueUrl?: string;
    initialRole?: Role;
    birthdate?: string;
    referral?: string;
    onRoleChange: (role: Role) => void;
    onBirthdateChange: (birthdate: string, under13: boolean) => void;
    // Used by ProgressShell to coordinate which screens to render.
    updateStep: UpdateStep;
};

function SignupIntroActionScreen(
    props: Props,
): React.ReactElement<React.ComponentProps<"div">> {
    const {
        referral,
        initialRole,
        birthdate,
        continueUrl,
        onRoleChange,
        onBirthdateChange,
        updateStep,
    } = props;

    const [role, setRole] = React.useState(initialRole || "learner");
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    function updateRole(newRole: string) {
        const roleToUpdateTo = ROLES.find((r) => newRole === r);
        if (roleToUpdateTo && role !== roleToUpdateTo) {
            publishAnalyticsEvent(
                {
                    eventType: "SignupRoleSwitched",
                    eventSchemaVersion: 1,
                    newRole: cedarRole[roleToUpdateTo],
                    originalRole: cedarRole[role],
                },
                // NOTE: we cannot add the legacy bingo conversion id here
                // it's a minor conversion.
            );
            bbMarkMinorConversion("signup_switched_roles", {
                originalRole: role,
                newRole: roleToUpdateTo,
            });
            onRoleChange(roleToUpdateTo);
            setRole(roleToUpdateTo);
        }
    }

    function renderRoleSelection(): React.ReactElement<
        React.ComponentProps<"div">
    > {
        let preamble =
            RequestInfo.kaLocale === "en"
                ? t`Join Khan Academy for free as a`
                : t`Join Khan Academy as a`;
        let subText = "";
        let showButtonToggle = true;
        let signupOptional = false;
        if (role === "parent") {
            switch (referral) {
                case "parent_approval_success":
                    preamble = t`Create your parent account`;
                    subText = t`We’ll keep you up-to-date with what your child is learning on Khan Academy.`;
                    showButtonToggle = false;
                    signupOptional = true;
                    break;
                case "parent_approval_failed":
                    preamble = t`Create your parent account`;
                    subText = t`We’ll walk you through creating your child’s account and then keep you up-to-date with what they’re learning on Khan Academy.`;
                    showButtonToggle = false;
                    break;
            }
        }

        const ROLE_COPY = getRoleCopy();

        return (
            <div>
                <div>
                    <span
                        className={css(
                            // If there's no subText, we want to make the
                            // preamble normal weight.
                            subText === "" ? styles.normalFontWeight : null,
                        )}
                    >
                        {preamble}
                    </span>
                    {signupOptional && (
                        <span className={css(styles.preambleOptional)}>
                            {t`Optional`}
                        </span>
                    )}
                </div>
                {subText && (
                    <div className={css(styles.preambleSubText)}>{subText}</div>
                )}
                {showButtonToggle && (
                    <DeprecatedButtonToggle
                        selected={role}
                        options={ROLES.map((role) => ({
                            value: role,
                            label: ROLE_COPY[role],
                        }))}
                        onToggle={(newRole) => {
                            updateRole(newRole);
                        }}
                        buttonStyles={styles.toggleButtonStyles}
                    />
                )}
                {renderFreeKhanmigoOnlyForGrassrootsTeacher()}
            </div>
        );
    }

    function renderFreeKhanmigoOnlyForGrassrootsTeacher() {
        if (isKhanmigoSignupLearnerParentSignup(role, referral)) {
            return (
                <LabelXSmall tag="p" style={{marginTop: -12}}>
                    {role === "learner" &&
                        t`*Learners require a recurring payment for Khanmigo.`}
                    {role === "parent" &&
                        t`*Parents require a recurring payment for Khanmigo.`}
                </LabelXSmall>
            );
        }
    }

    return (
        <ProgressShellActionScreen maxWidth={433}>
            <AuthTitle>{t`Sign up`}</AuthTitle>
            {renderRoleSelection()}
            <Strut size={spacing.small_12} />

            <BirthdayAndSignupSubform
                continueUrl={continueUrl}
                role={role}
                birthdate={birthdate}
                onBirthdateChange={onBirthdateChange}
                updateStep={updateStep}
            />
            <Strut size={spacing.xLarge_32} />
            {
                // restrict this to only 'en' and 'pt' for now until all translations are in
                ["en", "pt"].includes(RequestInfo.kaLocale) &&
                    role === "learner" && (
                        <div className={css(styles.classCode)}>
                            <Link
                                href="/join"
                                onClick={() =>
                                    bbMarkMinorConversion(
                                        "signup_enter_class_code_clicked",
                                    )
                                }
                            >
                                {t`Enter class code`}
                            </Link>
                        </div>
                    )
            }
            <div className={css(styles.login)}>
                <Link
                    href={preserveSearchParams(
                        getLoginUrl(),
                        new URLSearchParams(location.search),
                    )}
                    onClick={() =>
                        bbMarkMinorConversion("signup_login_link_clicked")
                    }
                >
                    {t`Already have an account?`}
                </Link>
            </div>
        </ProgressShellActionScreen>
    );
}

// Helper func to mark a single minor conversion with optional extra data.
function bbMarkMinorConversion(
    id: MinorConversionId,
    extra?: Record<string, unknown>,
) {
    const conversion = {id};
    if (extra) {
        conversion["extra"] = extra;
    }
    markMinorConversions([conversion]);
}

const styles = StyleSheet.create({
    preambleOptional: {
        float: "right",
        fontSize: 12,
        fontStyle: "italic",
        lineHeight: "22.5px",
        fontWeight: 400,
    },
    preambleSubText: {
        fontSize: 15,
        fontWeight: 400,
        marginTop: 5,
        marginBottom: 8,
    },
    classCode: {
        marginTop: 14,
        marginBottom: -14,
        textAlign: "center",
        fontSize: styleConstants.BODY_FONT_SIZE,
        color: color.green,
    },
    login: {
        marginTop: 24,
        marginBottom: 72,
        textAlign: "center",
        fontSize: styleConstants.BODY_FONT_SIZE,
        color: color.green,
    },
    toggleButtonStyles: {
        fontFamily: "inherit",
    },
    normalFontWeight: {
        fontWeight: "normal",
    },
});

export default SignupIntroActionScreen;
